// Fee types

export type FeeType = "NEW" | "USED" | "ALL";

export interface Fee {
  id: string;
  name: string;
  amount: number | null;
  removed: boolean;
  type: FeeType;
}

// API Request/Response types

export interface GetFeesRequest {
  accountId: string;
}

export interface GetFeesResponse {
  fees: Fee[];
}

export interface SaveFeesRequest {
  accountId: string;
  fees: Fee[];
  userId: string;
}

export type SaveFeesResponse =
  | { success: true; fees: Fee[] }
  | { success: false; error: "CONFLICT" };

// Config types

export interface FeeManagerConfig {
  emitItemizedFees: boolean;
  emitTotalFees: boolean;
  emitPriceAfterFees: boolean;
  priceAfterFeesInputAttribute:
    | "askingPrice"
    | "internetPrice"
    | "invoicePrice"
    | "msrp"
    | "retailValue"
    | "salePrice"
    | "wholesalePrice";
  applyOnInventoryItemSave: boolean;
  [key: string]: unknown;
}

export type SaveConfigResponse =
  | { success: true; config: FeeManagerConfig }
  | { success: false; error: "CONFLICT" };

export interface SaveConfigRequest {
  accountId: string;
  config: FeeManagerConfig;
  userId: string;
}

// Tenant types and configuration schema

export type TenantType = "DDC";

type FeeManagerConfigKnownFields = Pick<
  FeeManagerConfig,
  | "applyOnInventoryItemSave"
  | "emitItemizedFees"
  | "emitPriceAfterFees"
  | "emitTotalFees"
  | "priceAfterFeesInputAttribute"
>;

export type FeeManagerConfigFieldKey = keyof FeeManagerConfigKnownFields;

export interface FeeManagerConfigFieldSchema<TValue> {
  defaultValue: TValue;
  labelKey: string;
}

export type FeeManagerTenantSchema = {
  [TKey in FeeManagerConfigFieldKey]: FeeManagerConfigFieldSchema<
    FeeManagerConfigKnownFields[TKey]
  >;
};

export const TENANT_SCHEMAS: Record<TenantType, FeeManagerTenantSchema> = {
  DDC: {
    applyOnInventoryItemSave: {
      defaultValue: false,
      labelKey: "APPLY_ON_INVENTORY_ITEM_SAVE"
    },
    emitItemizedFees: { defaultValue: false, labelKey: "EMIT_ITEMIZED_FEES" },
    emitPriceAfterFees: {
      defaultValue: false,
      labelKey: "EMIT_PRICE_AFTER_FEES"
    },
    emitTotalFees: { defaultValue: false, labelKey: "EMIT_TOTAL_FEES" },
    priceAfterFeesInputAttribute: {
      defaultValue: "askingPrice",
      labelKey: "PRICE_AFTER_FEES_INPUT_ATTRIBUTE"
    }
  }
};

export const getConfigDefaults = (tenantType: TenantType): FeeManagerConfig => {
  const schema = TENANT_SCHEMAS[tenantType];
  return Object.fromEntries(
    (Object.keys(schema) as FeeManagerConfigFieldKey[]).map(key => [
      key,
      schema[key].defaultValue
    ])
  ) as FeeManagerConfig;
};

// Fetch interface for hooks

export interface FeeManagerFetchInterface {
  accountId: string;
  disabled?: boolean;
}

export interface FeeManagerFetchWriteInterface {
  accountId: string;
  /**
   * Called after a successful mutation.
   * - Function: called with the response data
   * - String: used as a sub-key to invalidate via queryClient.invalidateQueries({ queryKey: [queryKeyBase, afterSuccess] })
   */
  afterSuccess?: ((data: unknown) => void) | string;
}

// Type guards

export function isFee(object: unknown): object is Fee {
  if (!object || typeof object !== "object") return false;
  const obj = object as Record<string, unknown>;
  return (
    typeof obj.id === "string" &&
    typeof obj.name === "string" &&
    (typeof obj.amount === "number" || obj.amount === null) &&
    typeof obj.removed === "boolean" &&
    (obj.type === "NEW" || obj.type === "USED" || obj.type === "ALL")
  );
}

export function isGetFeesResponse(object: unknown): object is GetFeesResponse {
  if (!object || typeof object !== "object") return false;
  const obj = object as Record<string, unknown>;
  return Array.isArray(obj.fees);
}

export * from "./ddc-pages-fee-manager-interface-mock";
